import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LoadingState, PageHeader, PageShell } from "@/components/app/page";
import { adminAPI, adminKycAPI } from "@/services/api";
import { ADMIN_RANKS, getAdminRanks, hasFullAdminRank } from "@/utils/adminRanks";
import { formatCurrency, formatDate } from "@/utils/constants";
import { COUNTRIES, getCountryByCode, prefixPhoneWithDialCode } from "@/utils/countries";
import { getErrorMessage } from "@/utils/errorHelpers";
import ZoomableImage from "@/components/app/ZoomableImage";
import { toast } from "sonner";
import { Check, ChevronLeft, ChevronRight, GitBranch, History, Shield, Trash2, UserPlus, Users, Wallet } from "lucide-react";

const RANK_OPTIONS = [
  { value: ADMIN_RANKS.A, label: "A - KYC" },
  { value: ADMIN_RANKS.B, label: "B - Activation" },
  { value: ADMIN_RANKS.C, label: "C - Commandes" },
  { value: ADMIN_RANKS.GP, label: "GP - Gestionnaire de partenaire" },
  { value: ADMIN_RANKS.CL, label: "CL - Catalogue libre" },
  { value: ADMIN_RANKS.FULL, label: "FULL" },
];
const ASSIGNABLE_RANK_OPTIONS = RANK_OPTIONS.filter((rank) => rank.value !== ADMIN_RANKS.CL);
const CATALOGUE_LIBRE_ID = "ARTICO004";
const PARTNER_PAGE_SIZE = 15;

const initialForm = { id: "", full_name: "", email: "", password: "", telephone: "", pays: getCountryByCode("CD").name, country_code: "CD", admin_rank: [] };
const initialKycEdit = {
  nom: "", postnom: "", prenom: "", sexe: "", date_naissance: "", telephone: "",
  numero_identite: "", adresse_rue: "", adresse_numero: "", adresse_commune: "",
  adresse_ville: "", adresse_province: "", adresse_pays: "",
  mobile_money_intitule: "", mobile_money_numero: "", banque_nom: "",
  banque_succursale: "", banque_intitule: "", banque_numero: "",
  bénéficiaire_designation: "", reason: "",
};

export default function UserManagementPage({ user }) {
  const [users, setUsers] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [history, setHistory] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [rankEdits, setRankEdits] = useState({});
  const [partnerProfile, setPartnerProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [kycEdit, setKycEdit] = useState(initialKycEdit);
  const [kycSaving, setKycSaving] = useState(false);
  const [reportFrequency, setReportFrequency] = useState("monthly");
  const [reportPeriod, setReportPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [reporting, setReporting] = useState(false);
  const [criticalChanges, setCriticalChanges] = useState([]);
  const isFullAdmin = hasFullAdminRank(user);

  const admins = useMemo(() => (Array.isArray(users) ? users : []).filter((user) => user.role === "admin"), [users]);
  const safePartners = Array.isArray(partners) ? partners : [];
  const [partnerPage, setPartnerPage] = useState(1);
  const partnerPageCount = Math.max(1, Math.ceil(safePartners.length / PARTNER_PAGE_SIZE));
  const paginatedPartners = safePartners.slice((partnerPage - 1) * PARTNER_PAGE_SIZE, partnerPage * PARTNER_PAGE_SIZE);
  const pendingCriticalChanges = (Array.isArray(criticalChanges) ? criticalChanges : []).filter((change) => change.status === "pending_admin_validation");

  useEffect(() => {
    if (partnerPage > partnerPageCount) {
      setPartnerPage(partnerPageCount);
    }
  }, [partnerPage, partnerPageCount]);

  const loadHub = useCallback(async () => {
    try {
      const [usersRes, partnersRes, changesRes] = await Promise.all(
        isFullAdmin
          ? [adminAPI.listUsers(), adminAPI.listPartnerBase(), adminAPI.listCriticalChanges()]
          : [Promise.resolve({ data: [] }), adminAPI.listPartnerBase(), Promise.resolve({ data: [] })]
      );
      const nextUsers = Array.isArray(usersRes.data) ? usersRes.data : [];
      setUsers(nextUsers);
      setPartners(Array.isArray(partnersRes.data) ? partnersRes.data : []);
      setCriticalChanges(Array.isArray(changesRes.data) ? changesRes.data : []);
      const edits = {};
      nextUsers.filter((u) => u.role === "admin").forEach((u) => { edits[u.id] = getAdminRanks(u); });
      setRankEdits(edits);
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur chargement hub"));
    } finally {
      setLoading(false);
    }
  }, [isFullAdmin]);

  useEffect(() => { loadHub(); }, [loadHub]);

  const toggleCreateRank = (rank) => {
    setForm((prev) => ({
      ...prev,
      admin_rank: prev.admin_rank.includes(rank) ? prev.admin_rank.filter((r) => r !== rank) : [...prev.admin_rank, rank],
    }));
  };

  const selectAdminCountry = (code) => {
    const country = getCountryByCode(code);
    setForm((prev) => ({
      ...prev,
      country_code: country.code,
      pays: country.name,
      telephone: prefixPhoneWithDialCode(prev.telephone, country.dialCode),
    }));
  };

  const fillNextAdminId = async () => {
    try {
      const res = await adminAPI.getNextAdminId();
      setForm((prev) => ({ ...prev, id: res.data?.next_id || prev.id }));
    } catch (err) {
      toast.error(getErrorMessage(err, "Impossible de proposer l'ID suivant"));
    }
  };

  const toggleAgentRank = (userId, rank) => {
    setRankEdits((prev) => {
      const current = prev[userId] || [];
      return {
        ...prev,
        [userId]: current.includes(rank) ? current.filter((r) => r !== rank) : [...current, rank],
      };
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await adminAPI.createUser({ ...form, id: form.id.trim(), admin_rank: form.admin_rank });
      toast.success("administrateur créé");
      setForm(initialForm);
      loadHub();
    } catch (err) {
      toast.error(getErrorMessage(err, "Erreur creation administrateur"));
    } finally {
      setCreating(false);
    }
  };

  const saveRanks = async (adminUser) => {
    try {
      await adminAPI.updateUserRank(adminUser.id, { admin_rank: rankEdits[adminUser.id] || [] });
      toast.success("Modification proposee : validation FULL differente requise");
      loadHub();
    } catch (err) {
      toast.error(getErrorMessage(err, "Erreur mise a jour des rôles"));
    }
  };

  const approveCriticalChange = async (change) => {
    try {
      await adminAPI.approveCriticalChange(change.id, "Validation four-eyes FULL");
      toast.success("Modification approuvee");
      loadHub();
    } catch (err) {
      toast.error(getErrorMessage(err, "Approbation impossible"));
    }
  };

  const loadHistory = async (user) => {
    setHistoryTarget(user);
    try {
      const res = await adminAPI.getUserHistory(user.id);
      setHistory(Array.isArray(res.data?.history) ? res.data.history : []);
    } catch (err) {
      setHistory([]);
      toast.error(getErrorMessage(err, "Erreur historique"));
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget || !deletePassword) return;
    setDeleting(true);
    try {
      await adminAPI.deleteUser(deleteTarget.id, deletePassword);
      toast.success("Utilisateur supprimé");
      setDeleteTarget(null);
      setDeletePassword("");
      loadHub();
    } catch (err) {
      toast.error(getErrorMessage(err, "Suppression impossible"));
    } finally {
      setDeleting(false);
    }
  };

  const loadPartnerProfile = async (partner) => {
    setProfileLoading(true);
    try {
      const res = await adminAPI.getPartnerProfile(partner.id);
      const profile = res.data || {};
      setPartnerProfile(profile);
      const identity = profile.kyc?.identity || {};
      const address = identity.adresse || {};
      const financial = profile.kyc?.financial || {};
      setKycEdit({
        nom: identity.nom || "", postnom: identity.postnom || "", prenom: identity.prenom || "",
        sexe: identity.sexe || "", date_naissance: identity.date_naissance || "", telephone: identity.telephone || "",
        numero_identite: identity.numero_identite || "", adresse_rue: address.rue || "",
        adresse_numero: address.numéro || "", adresse_commune: address.commune || "",
        adresse_ville: address.ville || "", adresse_province: address.province || "",
        adresse_pays: address.pays || "", mobile_money_intitule: financial.mobile_money_intitule || "",
        mobile_money_numero: financial.mobile_money_numero || "", banque_nom: financial.banque_nom || "",
        banque_succursale: financial.banque_succursale || "", banque_intitule: financial.banque_intitule || "",
        banque_numero: financial.banque_numero || "", bénéficiaire_designation: profile.kyc?.bénéficiaire_designation || "",
        reason: "",
      });
    } catch (err) {
      toast.error(getErrorMessage(err, "Erreur profil partenaire"));
    } finally {
      setProfileLoading(false);
    }
  };

  const proposeKycUpdate = async (e) => {
    e.preventDefault();
    if (!partnerProfile?.partner) return;
    setKycSaving(true);
    try {
      await adminAPI.proposePartnerKycUpdate(partnerProfile.partner.id, kycEdit);
      toast.success("Modification proposee, validation FULL requise");
      setPartnerProfile(null);
    } catch (err) {
      toast.error(getErrorMessage(err, "Modification impossible"));
    } finally {
      setKycSaving(false);
    }
  };

  const requestPartnerDelete = async (partner) => {
    try {
      await adminAPI.requestPartnerDelete(partner.id);
      toast.success("Suppression proposee. Validation FULL requise.");
      await loadHub();
    } catch (err) {
      toast.error(getErrorMessage(err, "Demande de suppression impossible"));
    }
  };

  const downloadFinancialReport = async () => {
    setReporting(true);
    try {
      const res = await adminAPI.exportFinancialReport({ frequency: reportFrequency, period: reportPeriod });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `rapport_financier_${reportFrequency}_${reportPeriod}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(getErrorMessage(err, "Export impossible"));
    } finally {
      setReporting(false);
    }
  };

  if (loading) return <LoadingState label="Chargement du hub utilisateurs..." />;

  return (
    <PageShell data-testid="user-management-page">
      <PageHeader
        title={isFullAdmin ? "Gestion des Utilisateurs" : "Gestion des partenaires"}
        description={isFullAdmin ? "Hub interne FULL : creation admin, équipe administrative et base partenaires." : "Base partenaires, suivi administratif et demandes sensibles avec validation FULL."}
      />

      {isFullAdmin && <Card className="border-[#E6E4E0] shadow-sm mb-6">
        <CardContent className="grid gap-3 p-4 sm:grid-cols-2 lg:flex lg:items-end">
          <div>
            <Label>Frequence rapport Excel</Label>
            <select className="mt-1 block h-10 w-full rounded-xl border border-[#E6E4E0] px-3" value={reportFrequency} onChange={(e) => setReportFrequency(e.target.value)}>
              <option value="daily">Journalier</option>
              <option value="weekly">Hebdomadaire</option>
              <option value="monthly">Mensuel</option>
              <option value="quarterly">Trimestriel</option>
            </select>
          </div>
          <div>
            <Label>Periode</Label>
            <Input className="mt-1" value={reportPeriod} onChange={(e) => setReportPeriod(e.target.value)} placeholder="YYYY-MM / YYYY-MM-DD / YYYY-W22 / YYYY-Q1" />
          </div>
          <Button onClick={downloadFinancialReport} disabled={reporting} className="w-full bg-[#2A4B46] text-white sm:col-span-2 lg:w-auto">
            {reporting ? "Generation..." : "Tirer un rapport Excel"}
          </Button>
        </CardContent>
      </Card>}

      {isFullAdmin && pendingCriticalChanges.length > 0 && (
        <Card className="border-[#E6E4E0] shadow-sm mb-6 border-l-4 border-l-blue-500">
          <CardHeader><CardTitle className="text-xl text-[#2A4B46]">Validations four-eyes en attente</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {pendingCriticalChanges.map((change) => (
              <div key={change.id} className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div>
                  <p className="font-semibold text-[#2A4B46]">
                    {formatCriticalChangeTitle(change)} - {change.target_label || change.target_user?.full_name || change.target_id}
                  </p>
                  <p className="text-xs text-[#5C625F]">Statut: {change.status} | Propose par {change.proposed_by_name || change.proposed_by}</p>
                  <p className="text-xs text-[#5C625F]">{formatCriticalChangeDetails(change)}</p>
                </div>
                <Button size="sm" className="bg-green-700 text-white" onClick={() => approveCriticalChange(change)}>
                  <Check size={14} className="mr-1" /> valider FULL
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue={isFullAdmin ? "create" : "partners"} className="space-y-6">
        <TabsList className="flex h-auto w-full flex-wrap justify-start gap-2 bg-transparent p-0">
          {isFullAdmin && <TabsTrigger value="create"><UserPlus size={14} className="mr-2" /> Nouvel administrateur</TabsTrigger>}
          {isFullAdmin && <TabsTrigger value="admins"><Shield size={14} className="mr-2" /> équipe Administrative</TabsTrigger>}
          <TabsTrigger value="partners"><Users size={14} className="mr-2" /> Base Partenaires</TabsTrigger>
        </TabsList>

        {isFullAdmin && <TabsContent value="create">
          <Card className="border-[#E6E4E0] shadow-sm max-w-2xl">
            <CardHeader><CardTitle className="text-xl text-[#2A4B46]">Fiche de creation administrateur</CardTitle></CardHeader>
            <CardContent>
              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <Label>ID ARTICOXXX</Label>
                  <div className="mt-1 flex flex-col gap-2 sm:flex-row">
                    <Input value={form.id} onChange={(e) => setForm({ ...form, id: e.target.value.toUpperCase() })} placeholder="ARTICO001" pattern="ARTICO[0-9A-Z]{3,12}" required />
                    <Button type="button" variant="outline" onClick={fillNextAdminId} className="shrink-0">ID suivant</Button>
                  </div>
                </div>
                <div><Label>Nom complet</Label><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required /></div>
                <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required /></div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <Label>Pays</Label>
                    <Select value={form.country_code} onValueChange={selectAdminCountry}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent className="max-h-72">
                        {COUNTRIES.map((country) => (
                          <SelectItem key={country.code} value={country.code}>{country.name} ({country.dialCode})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Telephone</Label><Input value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} placeholder={getCountryByCode(form.country_code).dialCode} /></div>
                </div>
                <div><Label>Mot de passe</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required /></div>
                <div>
                  <Label>Rôles</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {ASSIGNABLE_RANK_OPTIONS.map((rank) => (
                      <label key={rank.value} className="flex items-center gap-2 p-2 rounded-lg border border-[#E6E4E0] text-sm">
                        <input type="checkbox" checked={form.admin_rank.includes(rank.value)} onChange={() => toggleCreateRank(rank.value)} />
                        {rank.label}
                      </label>
                    ))}
                  </div>
                </div>
                <Button type="submit" disabled={creating} className="w-full bg-[#2A4B46] text-white">{creating ? "Creation..." : "créer l'administrateur"}</Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>}

        {isFullAdmin && <TabsContent value="admins">
          <Card className="border-[#E6E4E0] shadow-sm">
            <CardHeader><CardTitle className="text-xl text-[#2A4B46]">équipe Administrative</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-3 md:hidden">
                {admins.map((adminUser) => (
                  <div key={adminUser.id} className="rounded-2xl border border-[#E6E4E0] bg-white p-4 shadow-sm">
                    <div className="space-y-1">
                      <p className="font-semibold text-[#2A4B46]">{adminUser.full_name}</p>
                      <p className="font-mono text-xs text-[#5C625F]">{adminUser.id}</p>
                      <p className="break-all text-sm text-[#5C625F]">{adminUser.email}</p>
                      <p className="text-sm text-[#5C625F]">{adminUser.telephone || "-"} | {adminUser.pays || "-"}</p>
                    </div>
                    <div className="mt-4 grid grid-cols-2 gap-2">
                      {(adminUser.id === CATALOGUE_LIBRE_ID ? RANK_OPTIONS : ASSIGNABLE_RANK_OPTIONS).map((rank) => (
                        <label key={rank.value} className="flex items-center gap-2 rounded-xl border border-[#E6E4E0] px-3 py-2 text-xs">
                          <input type="checkbox" disabled={rank.value === ADMIN_RANKS.CL} checked={(rankEdits[adminUser.id] || []).includes(rank.value)} onChange={() => toggleAgentRank(adminUser.id, rank.value)} />
                          {rank.label}
                        </label>
                      ))}
                    </div>
                    <div className="mt-4 grid gap-2">
                      <Button variant="outline" size="sm" onClick={() => saveRanks(adminUser)}><Check size={14} className="mr-1" /> Rôles</Button>
                      <Button variant="outline" size="sm" onClick={() => loadHistory(adminUser)}><History size={14} className="mr-1" /> Audit</Button>
                      <Button variant="outline" size="sm" className="text-red-700" onClick={() => setDeleteTarget(adminUser)}><Trash2 size={14} className="mr-1" /> Supprimer</Button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="hidden overflow-x-auto md:block">
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left text-[#5C625F]"><th className="py-2">ID</th><th>Nom</th><th>Email</th><th>Telephone</th><th>Pays</th><th>Rôles</th><th className="text-right">Actions</th></tr></thead>
                <tbody>
                  {admins.map((adminUser) => (
                    <tr key={adminUser.id} className="border-b border-[#F3EFE9] align-top">
                      <td className="py-3 font-mono text-xs">{adminUser.id}</td>
                      <td className="py-3 font-semibold text-[#2A4B46]">{adminUser.full_name}</td>
                      <td className="py-3 text-[#5C625F]">{adminUser.email}</td>
                      <td className="py-3 text-[#5C625F]">{adminUser.telephone || "-"}</td>
                      <td className="py-3 text-[#5C625F]">{adminUser.pays || "-"}</td>
                      <td className="py-3">
                        <div className="flex flex-wrap gap-2">
                          {(adminUser.id === CATALOGUE_LIBRE_ID ? RANK_OPTIONS : ASSIGNABLE_RANK_OPTIONS).map((rank) => (
                            <label key={rank.value} className="flex items-center gap-1 text-xs">
                              <input type="checkbox" disabled={rank.value === ADMIN_RANKS.CL} checked={(rankEdits[adminUser.id] || []).includes(rank.value)} onChange={() => toggleAgentRank(adminUser.id, rank.value)} />
                              {rank.value}
                            </label>
                          ))}
                        </div>
                      </td>
                      <td className="py-3">
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" size="sm" onClick={() => saveRanks(adminUser)}><Check size={14} className="mr-1" /> Rôles</Button>
                          <Button variant="outline" size="sm" onClick={() => loadHistory(adminUser)}><History size={14} className="mr-1" /> Audit</Button>
                          <Button variant="outline" size="sm" className="text-red-700" onClick={() => setDeleteTarget(adminUser)}><Trash2 size={14} className="mr-1" /> Supprimer</Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>}

        <TabsContent value="partners">
          <Card className="border-[#E6E4E0] shadow-sm">
            <CardHeader>
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <CardTitle className="text-xl text-[#2A4B46]">Base Partenaires</CardTitle>
                <PartnerPagination
                  page={partnerPage}
                  pageCount={partnerPageCount}
                  total={safePartners.length}
                  setPage={setPartnerPage}
                />
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-3 md:hidden">
                {paginatedPartners.map((partner) => (
                  <div key={partner.id} className="rounded-2xl border border-[#E6E4E0] bg-white p-4 shadow-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-[#2A4B46]">{partner.full_name}</p>
                        <p className="font-mono text-xs text-[#5C625F]">{partner.id}</p>
                      </div>
                      <Badge className="bg-[#F3EFE9] text-[#2A4B46]">{partner.partner_level || "P-00"}</Badge>
                    </div>
                    <div className="mt-4 grid grid-cols-3 gap-2 text-center text-xs">
                      <div className="rounded-xl bg-[#F9F7F4] p-2"><p className="text-[#5C625F]">Ascension</p><p className="font-bold text-[#2A4B46]">{partner.points_ascension || 0}</p></div>
                      <div className="rounded-xl bg-[#F9F7F4] p-2"><p className="text-[#5C625F]">PAE</p><p className="font-bold text-[#2A4B46]">{partner.is_active_pae ? "Actif" : "Inactif"}</p></div>
                      <div className="rounded-xl bg-[#F9F7F4] p-2"><p className="text-[#5C625F]">PAR</p><p className="font-bold text-[#2A4B46]">{partner.is_active_par ? "Actif" : "Inactif"}</p></div>
                    </div>
                    <div className="mt-3 space-y-1 text-xs text-[#5C625F]">
                      <p><strong>Email:</strong> {partner.email || "-"}</p>
                      <p><strong>ID du parrain:</strong> <span className="font-mono">{partner.parrain_id || "-"}</span></p>
                      <p><strong>KYC:</strong> {partner.is_verified ? "validé" : "Non validé"} | <strong>Email:</strong> {partner.email_verified ? "Verifie" : "Non verifie"}</p>
                      <p><strong>PM:</strong> {partner.points_monnaie || 0} | <strong>Activation:</strong> {partner.points_activation || 0} | <strong>équipe:</strong> {partner.points_equipe || 0}</p>
                      <p><strong>Inscription:</strong> {formatDate(partner.created_at)}</p>
                    </div>
                    <div className="mt-4 grid gap-2 sm:grid-cols-2">
                      <Button variant="outline" size="sm" onClick={() => loadPartnerProfile(partner)}>Ouvrir</Button>
                      <Button asChild variant="outline" size="sm">
                        <Link to={`/admin/partners/${encodeURIComponent(partner.id)}/lineage`}>
                          <GitBranch size={14} className="mr-1" /> Afficher la lignee
                        </Link>
                      </Button>
                      <Button variant="outline" size="sm" className="text-red-700" onClick={() => requestPartnerDelete(partner)}>
                        <Trash2 size={14} className="mr-1" /> Supprimer
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="hidden max-h-[680px] overflow-auto rounded-2xl border border-[#E6E4E0] md:block">
              <table className="w-full min-w-[1180px] text-sm">
                <thead className="sticky top-0 z-10 bg-[#F9F7F4]"><tr className="border-b text-left text-[#5C625F]"><th className="py-3 pl-4">ID</th><th>Nom</th><th>Email</th><th>ID du parrain</th><th>Niveau</th><th>Points</th><th>Statuts</th><th>Inscription</th><th className="pr-4 text-right">Actions</th></tr></thead>
                <tbody>
                  {paginatedPartners.map((partner) => (
                    <tr key={partner.id} className="border-b border-[#F3EFE9]">
                      <td className="py-3 pl-4 font-mono text-xs">{partner.id}</td>
                      <td className="py-3 font-semibold text-[#2A4B46]">{partner.full_name}</td>
                      <td className="text-xs">{partner.email || "-"}</td>
                      <td className="font-mono text-xs">{partner.parrain_id || "-"}</td>
                      <td><Badge className="bg-[#F3EFE9] text-[#2A4B46]">{partner.partner_level || "P-00"}</Badge></td>
                      <td className="text-xs">
                        <p>PM: {partner.points_monnaie || 0}</p>
                        <p>Activation: {partner.points_activation || 0}</p>
                        <p>équipe: {partner.points_equipe || 0}</p>
                        <p>Ascension: {partner.points_ascension || 0}</p>
                      </td>
                      <td className="text-xs">
                        <p>PAE: {partner.is_active_pae ? "Actif" : "Inactif"}</p>
                        <p>PAR: {partner.is_active_par ? "Actif" : "Inactif"}</p>
                        <p>KYC: {partner.is_verified ? "validé" : "Non validé"}</p>
                        <p>Email: {partner.email_verified ? "Verifie" : "Non verifie"}</p>
                      </td>
                      <td className="text-xs">{formatDate(partner.created_at)}</td>
                      <td className="pr-4 text-right">
                        <div className="flex flex-wrap justify-end gap-2">
                          <Button variant="outline" size="sm" onClick={() => loadPartnerProfile(partner)}>Ouvrir</Button>
                          <Button asChild variant="outline" size="sm">
                            <Link to={`/admin/partners/${encodeURIComponent(partner.id)}/lineage`}>
                              <GitBranch size={14} className="mr-1" /> Lignee
                            </Link>
                          </Button>
                          <Button variant="outline" size="sm" className="text-red-700" onClick={() => requestPartnerDelete(partner)}>
                            <Trash2 size={14} className="mr-1" /> Supprimer
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              <PartnerPagination
                page={partnerPage}
                pageCount={partnerPageCount}
                total={safePartners.length}
                setPage={setPartnerPage}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <HistoryDialog target={historyTarget} history={history} close={() => setHistoryTarget(null)} />
      <DeleteDialog target={deleteTarget} password={deletePassword} setPassword={setDeletePassword} deleting={deleting} close={() => setDeleteTarget(null)} confirm={confirmDelete} />
      <PartnerProfileDialog profile={partnerProfile} loading={profileLoading} close={() => setPartnerProfile(null)} kycEdit={kycEdit} setKycEdit={setKycEdit} proposeKycUpdate={proposeKycUpdate} saving={kycSaving} requestPartnerDelete={requestPartnerDelete} />
    </PageShell>
  );
}

function PartnerPagination({ page, pageCount, total, setPage }) {
  const start = total === 0 ? 0 : (page - 1) * PARTNER_PAGE_SIZE + 1;
  const end = Math.min(total, page * PARTNER_PAGE_SIZE);
  return (
    <div className="flex flex-col gap-2 text-xs text-[#5C625F] sm:flex-row sm:items-center sm:justify-between">
      <span>{start}-{end} sur {total} partenaire(s)</span>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={page <= 1}
          onClick={() => setPage((current) => Math.max(1, current - 1))}
        >
          <ChevronLeft size={14} className="mr-1" /> Precedent
        </Button>
        <span className="min-w-16 text-center font-semibold text-[#2A4B46]">{page}/{pageCount}</span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={page >= pageCount}
          onClick={() => setPage((current) => Math.min(pageCount, current + 1))}
        >
          Suivant <ChevronRight size={14} className="ml-1" />
        </Button>
      </div>
    </div>
  );
}

function HistoryDialog({ target, history, close }) {
  const safeHistory = Array.isArray(history) ? history : [];
  return (
    <Dialog open={!!target} onOpenChange={(open) => !open && close()}>
      <DialogContent className="max-h-[92dvh] w-[calc(100vw-1.5rem)] overflow-y-auto sm:max-w-3xl">
        <DialogHeader><DialogTitle>Historique - {target?.full_name}</DialogTitle></DialogHeader>
        {safeHistory.length === 0 ? <p className="text-sm text-[#5C625F] py-6">Aucune action auditee.</p> : (
          <div className="space-y-2">
            {safeHistory.map((log) => (
              <div key={log.id} className="p-3 border border-[#E6E4E0] rounded-lg">
                <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between"><p className="font-semibold text-[#2A4B46]">{log.action}</p><span className="text-xs text-[#5C625F]">{formatDate(log.created_at)}</span></div>
                <p className="text-xs text-[#5C625F] mt-1">Cible: {log.target_type} / {log.target_id}</p>
                {log.détails && <pre className="mt-2 text-xs bg-[#F9F7F4] p-2 rounded overflow-x-auto">{JSON.stringify(log.détails, null, 2)}</pre>}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DeleteDialog({ target, password, setPassword, deleting, close, confirm }) {
  return (
    <Dialog open={!!target} onOpenChange={(open) => !open && close()}>
      <DialogContent className="w-[calc(100vw-1.5rem)] sm:max-w-md">
        <DialogHeader><DialogTitle>Confirmer la suppression</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-[#5C625F]">Suppression de <strong>{target?.full_name}</strong> ({target?.id}). Saisis ton mot de passe FULL.</p>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Mot de passe administrateur" />
          <Button className="w-full bg-red-700 text-white" disabled={deleting || !password} onClick={confirm}>{deleting ? "Suppression..." : "Supprimer definitivement"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PartnerProfileDialog({ profile, loading, close, kycEdit, setKycEdit, proposeKycUpdate, saving, requestPartnerDelete }) {
  const kyc = profile?.kyc;
  const partner = profile?.partner;
  return (
    <Dialog open={!!profile || loading} onOpenChange={(open) => !open && close()}>
      <DialogContent className="h-[100dvh] w-screen max-w-none overflow-y-auto rounded-none sm:h-auto sm:max-h-[90vh] sm:max-w-6xl sm:rounded-2xl">
        <DialogHeader><DialogTitle>Profil partenaire - {partner?.full_name || ""}</DialogTitle></DialogHeader>
        {loading || !profile ? <p className="py-8 text-center">Chargement...</p> : !partner ? (
          <p className="py-8 text-center text-sm text-[#5C625F]">Profil partenaire indisponible.</p>
        ) : (
          <Tabs defaultValue="identity" className="space-y-4">
            <TabsList className="flex h-auto flex-wrap justify-start gap-2 bg-[#F3EFE9] p-1">
              <TabsTrigger value="identity">Identite</TabsTrigger>
              <TabsTrigger value="history">Historique</TabsTrigger>
              <TabsTrigger value="finance"><Wallet size={14} className="mr-1" /> Banque/KYC</TabsTrigger>
            </TabsList>
            <TabsContent value="identity">
              <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
                <div className="space-y-2 text-sm">
                  <p><strong>ID:</strong> {partner.id}</p>
                  <p><strong>Nom compte:</strong> {partner.full_name || "-"}</p>
                  <p><strong>Email:</strong> {partner.email || "-"}</p>
                  <p><strong>Email verifie:</strong> {partner.email_verified ? `Oui${partner.email_verified_at ? ` (${formatDate(partner.email_verified_at)})` : ""}` : "Non"}</p>
                  <p><strong>Role:</strong> {partner.role || "-"}</p>
                  <p><strong>Niveau:</strong> {partner.partner_level || "P-00"}</p>
                  <p><strong>Parrain:</strong> {partner.parrain_id || "-"}</p>
                  <p><strong>KYC:</strong> {partner.is_verified ? "validé" : "Non validé"}</p>
                  <p><strong>PAE:</strong> {partner.is_active_pae ? "Actif" : "Inactif"} | <strong>PAR:</strong> {partner.is_active_par ? "Actif" : "Inactif"}</p>
                  <p><strong>Points monnaie:</strong> {formatCurrency(partner.points_monnaie || 0)}</p>
                  <p><strong>Points activation:</strong> {partner.points_activation || 0} MC</p>
                  <p><strong>Points équipe:</strong> {partner.points_equipe || 0} MC</p>
                  <p><strong>Points ascension:</strong> {partner.points_ascension || 0} MC</p>
                  <p><strong>Compte créé le:</strong> {formatDate(partner.created_at)}</p>
                  <p><strong>Nom:</strong> {kyc?.identity?.nom} {kyc?.identity?.postnom} {kyc?.identity?.prenom}</p>
                  <p><strong>Sexe:</strong> {kyc?.identity?.sexe || "-"}</p>
                  <p><strong>Naissance:</strong> {kyc?.identity?.date_naissance || "-"}</p>
                  <p><strong>Telephone:</strong> {kyc?.identity?.telephone || "-"}</p>
                  <p><strong>Piece identite:</strong> {kyc?.identity?.numero_identite || "-"} ({kyc?.identity?.type_identite || "-"})</p>
                  <p><strong>Adresse:</strong> {formatAddress(kyc?.identity?.adresse)}</p>
                  <p><strong>Tutelle:</strong> {kyc?.is_minor ? "Mineur avec tuteur" : "Non mineur"}</p>
                  {kyc?.is_minor && <p><strong>Tuteur:</strong> {kyc?.tuteur?.nom} {kyc?.tuteur?.postnom} {kyc?.tuteur?.prenom} - {kyc?.tuteur?.telephone}</p>}
                  <p><strong>bénéficiaire designe:</strong> {kyc?.bénéficiaire_designation || "-"}</p>
                  <p><strong>Soumission KYC:</strong> {formatDate(kyc?.created_at)}</p>
                  <p><strong>Validation KYC:</strong> {formatDate(kyc?.validated_at)} {kyc?.validated_by ? `par ${kyc.validated_by}` : ""}</p>
                  <Button
                    type="button"
                    variant="outline"
                    className="mt-4 text-red-700"
                    onClick={() => requestPartnerDelete(partner)}
                  >
                    <Trash2 size={14} className="mr-1" /> Proposer la suppression du partenaire
                  </Button>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  {kyc?.id_photo_path ? <ZoomableImage src={adminKycAPI.getPhotoUrl(kyc.id, "id")} alt="Pièce d'identité" dialogLabel="Pièce d'identité" className="h-40 w-full rounded border" /> : <EmptyPhoto label="Piece ID" />}
                  {kyc?.profile_photo_path ? <ZoomableImage src={adminKycAPI.getPhotoUrl(kyc.id, "profile")} alt="Photo de profil" dialogLabel="Photo de profil" className="h-40 w-full rounded border" /> : <EmptyPhoto label="Profil" />}
                </div>
              </div>
            </TabsContent>
            <TabsContent value="history">
              <div className="grid lg:grid-cols-2 gap-4">
                <HistoryList title="Messages KYC" rows={profile.kyc_messages} render={(row) => `${row.sender_name || "-"}: ${row.message || "-"}`} />
                <HistoryList title="Transactions" rows={profile.transactions} render={(row) => `${row.type || row.bonus_type || "transaction"} - ${formatCurrency(row.amount || 0)} - ${row.id || row.transaction_id || ""}`} />
                <HistoryList title="Produits achetes" rows={profile.orders} render={(row) => `${row.product_title || row.product_id || "-"} - ${formatCurrency(row.price_mc || 0)} - ${row.status || ""}`} />
                <HistoryList title="Audit" rows={profile.audit_logs} render={(row) => `${row.action || "-"} - ${row.target_type || "-"}/${row.target_id || "-"}`} />
              </div>
            </TabsContent>
            <TabsContent value="finance">
              <form onSubmit={proposeKycUpdate} className="space-y-4">
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  <Field label="Nom" value={kycEdit.nom} onChange={(v) => setKycEdit({ ...kycEdit, nom: v })} />
                  <Field label="Post-nom" value={kycEdit.postnom} onChange={(v) => setKycEdit({ ...kycEdit, postnom: v })} />
                  <Field label="Prenom" value={kycEdit.prenom} onChange={(v) => setKycEdit({ ...kycEdit, prenom: v })} />
                  <Field label="Sexe" value={kycEdit.sexe} onChange={(v) => setKycEdit({ ...kycEdit, sexe: v })} />
                  <Field label="Date naissance" value={kycEdit.date_naissance} onChange={(v) => setKycEdit({ ...kycEdit, date_naissance: v })} />
                  <Field label="Telephone" value={kycEdit.telephone} onChange={(v) => setKycEdit({ ...kycEdit, telephone: v })} />
                </div>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  <Field label="Rue" value={kycEdit.adresse_rue} onChange={(v) => setKycEdit({ ...kycEdit, adresse_rue: v })} />
                  <Field label="numéro" value={kycEdit.adresse_numero} onChange={(v) => setKycEdit({ ...kycEdit, adresse_numero: v })} />
                  <Field label="Commune" value={kycEdit.adresse_commune} onChange={(v) => setKycEdit({ ...kycEdit, adresse_commune: v })} />
                  <Field label="Ville" value={kycEdit.adresse_ville} onChange={(v) => setKycEdit({ ...kycEdit, adresse_ville: v })} />
                  <Field label="Province" value={kycEdit.adresse_province} onChange={(v) => setKycEdit({ ...kycEdit, adresse_province: v })} />
                  <Field label="Pays" value={kycEdit.adresse_pays} onChange={(v) => setKycEdit({ ...kycEdit, adresse_pays: v })} />
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Mobile Money - Intitule" value={kycEdit.mobile_money_intitule} onChange={(v) => setKycEdit({ ...kycEdit, mobile_money_intitule: v })} />
                  <Field label="Mobile Money - numéro" value={kycEdit.mobile_money_numero} onChange={(v) => setKycEdit({ ...kycEdit, mobile_money_numero: v })} />
                  <Field label="Banque - Nom" value={kycEdit.banque_nom} onChange={(v) => setKycEdit({ ...kycEdit, banque_nom: v })} />
                  <Field label="Banque - Succursale" value={kycEdit.banque_succursale} onChange={(v) => setKycEdit({ ...kycEdit, banque_succursale: v })} />
                  <Field label="Banque - Intitule" value={kycEdit.banque_intitule} onChange={(v) => setKycEdit({ ...kycEdit, banque_intitule: v })} />
                  <Field label="Banque - numéro compte" value={kycEdit.banque_numero} onChange={(v) => setKycEdit({ ...kycEdit, banque_numero: v })} />
                </div>
                <Field label="bénéficiaire designe" value={kycEdit.bénéficiaire_designation} onChange={(v) => setKycEdit({ ...kycEdit, bénéficiaire_designation: v })} />
                <Field label="Motif audit" value={kycEdit.reason} onChange={(v) => setKycEdit({ ...kycEdit, reason: v })} />
                <p className="text-xs text-[#5C625F]">Toute modification reste en attente et doit etre approuvee par un admin FULL different.</p>
                <Button type="submit" disabled={saving} className="w-full bg-[#2A4B46] text-white sm:w-auto">{saving ? "Proposition..." : "Proposer la modification"}</Button>
              </form>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, onChange }) {
  return <div><Label>{label}</Label><Input value={value || ""} onChange={(e) => onChange(e.target.value)} /></div>;
}

function formatCriticalChangeTitle(change = {}) {
  const labels = {
    admin_rank: "Modification des rôles",
    platform_settings: "Informations ARTICO-MMJ",
    product_free_access: "accès libre produit",
    partner_delete: "Suppression partenaire",
  };
  return labels[change.change_type] || change.change_type || "Modification sensible";
}

function formatCriticalChangeDetails(change = {}) {
  const requested = change.requested_changes || {};
  if (change.change_type === "admin_rank") {
    return `Nouveaux rôles: ${(requested.admin_rank || []).join(", ") || "-"}`;
  }
  if (change.change_type === "platform_settings") {
    const fields = Object.keys(requested).filter((key) => key !== "notes");
    return `Champs proposes: ${fields.join(", ") || "-"}${requested.notes ? ` | Note: ${requested.notes}` : ""}`;
  }
  return `Champs proposes: ${Object.keys(requested).join(", ") || "-"}`;
}

function EmptyPhoto({ label }) {
  return <div className="h-40 flex items-center justify-center rounded border text-xs text-[#5C625F]">{label}</div>;
}

function HistoryList({ title, rows, render }) {
  return (
    <div className="border border-[#E6E4E0] rounded-lg p-3">
      <h3 className="font-semibold text-[#2A4B46] mb-2">{title} ({rows?.length || 0})</h3>
      <div className="space-y-2 max-h-72 overflow-y-auto">
        {(rows || []).length === 0 ? <p className="text-xs text-[#5C625F]">Aucun element.</p> : rows.map((row, index) => (
          <div key={row.id || row.transaction_id || index} className="text-xs bg-[#F9F7F4] rounded p-2">
            <p>{render(row)}</p>
            <p className="text-[#5C625F]">{formatDate(row.created_at || row.validated_at || "")}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function formatAddress(address = {}) {
  return [address.rue, address.numéro, address.commune, address.ville, address.province, address.pays].filter(Boolean).join(", ") || "-";
}
